/**
 * Decisions for the home page showreel, kept free of the DOM so they can be
 * tested. home-reel.ts wires them to the video element.
 */

import type { IconName } from './icons';

export interface ReelConditions {
  /** The visitor asked their system for less motion. */
  reducedMotion: boolean;
  /** The visitor's browser is in data saver mode. */
  saveData: boolean;
}

/**
 * Autoplay only when the visitor has not asked for less motion or less data.
 * Otherwise the poster stays up until they press play.
 */
export function shouldAutoplay(conditions: ReelConditions): boolean {
  return !conditions.reducedMotion && !conditions.saveData;
}

/**
 * How the reel grows when the visitor asks for a bigger view:
 * - `dialog`: a larger player over a dimmed page (desktop).
 * - `fullscreen`: the player, with its own controls, fills the screen (phones and tablets).
 * - `video-fullscreen`: the system player fills the screen (iPhone, where only a
 *   video element can go full screen).
 */
export type ExpandMode = 'dialog' | 'fullscreen' | 'video-fullscreen';

export interface ExpandConditions {
  /** The main pointer is a finger. */
  touch: boolean;
  /** The viewport is phone or small tablet width. */
  narrow: boolean;
  /** Any element can be made full screen (Fullscreen API). */
  elementFullscreen: boolean;
  /** Only a video element can be made full screen (iOS Safari on iPhone). */
  videoFullscreen: boolean;
}

export function expandMode(conditions: ExpandConditions): ExpandMode {
  if (conditions.touch || conditions.narrow) {
    if (conditions.elementFullscreen) return 'fullscreen';
    if (conditions.videoFullscreen) return 'video-fullscreen';
  }
  return 'dialog';
}

export interface ReelState {
  playing: boolean;
  muted: boolean;
  expanded: boolean;
  mode: ExpandMode;
}

export interface ReelLabels {
  playIcon: IconName;
  playLabel: string;
  soundIcon: IconName;
  soundText: string;
  expandIcon: IconName;
  expandLabel: string;
}

/** What the controls show for the current state of the video. */
export function reelLabels(state: ReelState): ReelLabels {
  const fullscreen = state.mode !== 'dialog';
  return {
    playIcon: state.playing ? 'pause' : 'play',
    playLabel: state.playing ? 'Pause the reel' : 'Play the reel',
    soundIcon: state.muted ? 'volumeX' : 'volume2',
    soundText: state.muted ? 'Play with sound' : 'Mute',
    expandIcon: state.expanded ? 'minimize2' : 'maximize2',
    expandLabel: state.expanded
      ? fullscreen ? 'Exit full screen' : 'Make the reel smaller'
      : fullscreen ? 'Watch full screen' : 'Make the reel bigger',
  };
}
